<template>
  <div class="filter-group" :class="{'is-nested': level > 0, 'not-group': group.operator === 'NOT'}">
    <div class="group-header">
      <div class="operator-selector">
        <select :value="group.operator" class="operator-select form-control" @change="updateOperator">
          <option value="AND">{{ t("filter_group-and_all") }}</option>
          <option value="OR">{{ t("filter_group-or_any") }}</option>
          <option value="NOT">{{ t("filter_group-not_exclude") }}</option>
        </select>
      </div>

      <div class="group-actions">
        <div class="action-wrapper">
          <button
            type="button"
            @click="addFilter"
            class="btn btn-sm btn-outline-success btn-add-item"
            ref="addFilterBtn"
          >
            {{ t("filter_group-add_filter") }}
          </button>

          <div
            v-show="showFilterSelector"
            ref="filterSelector"
            class="filter-selector-dropdown"
            @click.stop
          >
            <div class="filter-selector-header">
              <h6>{{ t("filter_group-add_filter") }}</h6>
              <button type="button" class="btn-close" @click="closeFilterSelector"></button>
            </div>
            <div class="filter-selector-body">
              <div v-if="selectableFilterOptions.length === 0" class="no-filters">
                {{ t("filter_group-no_filters") }}
              </div>
              <ul v-else class="filter-selector-list">
                <li
                  v-for="filter in selectableFilterOptions"
                  :key="filter.name"
                  class="filter-selector-item"
                  @click="addFilterToGroup(filter)"
                >
                  {{ filter.label }}
                </li>
              </ul>
            </div>
          </div>
        </div>

        <button type="button" @click="addGroup" class="btn btn-sm btn-outline-success btn-add-item">
        {{ t("filter_group-add_group") }}
        </button>

        <button v-if="level > 0" type="button" @click="$emit('remove')" class="btn btn-sm btn-outline-danger btn-remove">
          <span class="fas fa-times"></span>
        </button>
      </div>
    </div>

    <div class="group-body">
      <div v-if="group.children.length === 0" class="empty-group">
        <div class="empty-message" slot="empty-state">
          <slot name="empty-state">
            {{ t("filter_group-empty_state") }}
          </slot>
        </div>
      </div>

      <div v-else class="filter-grid">
        <div
          v-for="(child, index) in group.children"
          :key="index"
          :class="{
            'filter-grid-item': child.type !== 'group' && !isFullWidthFilter(child.name),
            'filter-grid-item-full': child.type === 'group' || isFullWidthFilter(child.name),
          }"
        >
          <!-- Render nested filter group -->
          <filter-group
            v-if="child.type === 'group'"
            :group="child"
            :level="level + 1"
            :filter-options="filterOptions"
            @change="(group, options) => updateFromChildGroup(group, index, options)"
            @remove="removeChild(index)"
          />

          <!-- Render individual filter -->
          <div v-else class="filter-card">
            <div class="filter-card-header">
              <h5 class="filter-name">{{ getComponentLabel(child.name) }}</h5>
              <button
                type="button"
                @click="removeChild(index)"
                class="btn btn-icon"
                :aria-label="t('filter_group-remove_filter')"
              >
                <span class="fas fa-trash"></span>
              </button>
            </div>
            <div class="filter-card-body">
              <!-- Using component names -->
              <component
                :is="getComponentName(child.name)"
                :key="`filter-${index}-${child.name}`"
                :options="getComponentOptions(child.name)"
                :name="child.name"
                :currentValue="child.value"
                :allow-reset="true"
                :label="child.label"
                @changed="val => filterValueChanged(index, val)"
                @update:value="val => filterValueChanged(index, val)"
                @input="val => filterValueChanged(index, val)"
              ></component>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "filter-group",
  components: {},
  props: {
    group: {
      type: Object,
      required: true
    },
    level: {
      type: Number,
      default: 0
    },
    filterOptions: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      showFilterSelector: false,
      globalClickHandler: null
    };
  },
  watch: {
    showFilterSelector(val) {
      if (val) {
        this.$nextTick(() => {
          this.globalClickHandler = (e) => {
            const selectorEl = this.$refs.filterSelector;
            const addFilterBtn = this.$refs.addFilterBtn;
            if (selectorEl && !selectorEl.contains(e.target) &&
                (!addFilterBtn || !addFilterBtn.contains(e.target))) {
              this.closeFilterSelector();
            }
          };
          document.addEventListener("click", this.globalClickHandler);
        });
      } else {
        this.removeGlobalClickHandler();
      }
    }
  },
  beforeUnmount() {
    this.removeGlobalClickHandler();
  },
  computed: {
    selectableFilterOptions() {
      return (this.filterOptions || []).filter((filter) => !filter.hidden);
    },
  },
  methods: {
    t: function (term, replaces) {
      return this.$root.t(term, replaces);
    },
    removeGlobalClickHandler() {
      if (this.globalClickHandler) {
        document.removeEventListener("click", this.globalClickHandler);
        this.globalClickHandler = null;
      }
    },

    // Helper method to safely clone objects
    safeClone(obj) {
      try {
        return JSON.parse(JSON.stringify(obj));
      } catch {
        // Fallback: create a shallow copy and remove Vue reactivity
        if (obj && typeof obj === "object") {
          return { ...obj };
        }
        return obj;
      }
    },

    getComponentName(name) {
      // First try exact match
      let found = this.filterOptions.find(opt => opt.name === name);
      if (found) return found.component;

      // If no exact match, try normalized match (preserve alphanumeric and underscores)
      const format = (str) => {
        return str.replace(/([^A-z0-9_])/g, "").toLowerCase();
      };
      found = this.filterOptions.find(opt => format(opt.name) === format(name));
      return found ? found.component : null;
    },
    getComponentLabel(name) {
      // First try exact match
      let found = this.filterOptions.find(opt => opt.name === name);
      if (found) return found.label;

      // If no exact match, try normalized match (preserve alphanumeric and underscores)
      const format = (str) => {
        return str.replace(/([^A-z0-9_])/g, "").toLowerCase();
      };
      found = this.filterOptions.find(opt => format(opt.name) === format(name));
      return found ? found.label : name;
    },
    getComponentOptions(name) {
      // First try exact match
      let found = this.filterOptions.find(opt => opt.name === name);
      if (found) return found.options;

      // If no exact match, try normalized match (preserve alphanumeric and underscores)
      const format = (str) => {
        return str.replace(/([^A-z0-9_])/g, "").toLowerCase();
      };
      found = this.filterOptions.find(opt => format(opt.name) === format(name));
      return found ? found.options : {};
    },
    isFullWidthFilter(name) {
      let found = this.filterOptions.find(opt => opt.name === name);
      if (found) return !!found.fullWidth;

      const format = (str) => str.replace(/([^A-z0-9_])/g, "").toLowerCase();
      found = this.filterOptions.find(opt => format(opt.name) === format(name));
      return found ? !!found.fullWidth : false;
    },
    addFilter() {
      this.showFilterSelector = !this.showFilterSelector;
    },

    addGroup() {
      const updatedGroup = this.safeClone(this.group);
      updatedGroup.children.push({
          type: "group",
          operator: "AND",
          children: []
      });
      this.$emit("change", updatedGroup, { updateEstimate: true });
    },

    removeChild(index) {
      const updatedGroup = this.safeClone(this.group);
      updatedGroup.children.splice(index, 1);
      this.$emit("change", updatedGroup, { updateEstimate: true });
    },

    filterValueChanged(index, value) {
      let actualValue;
      try {
          // Converter para JSON e depois de volta para objeto para remover propriedades reativas
          if (value && typeof value === "object") {
              if (Object.prototype.hasOwnProperty.call(value, "value")) {
                  // Se for um objeto com propriedade value
                  const jsonValue = JSON.stringify(value.value);
                  actualValue = jsonValue ? JSON.parse(jsonValue) : null;
              } else {
                  // Objeto normal
                  const jsonValue = JSON.stringify(value);
                  actualValue = jsonValue ? JSON.parse(jsonValue) : null;
              }
          } else {
              // Valor primitivo
              actualValue = value;
          }
      } catch {
          actualValue = value;
      }

      // Atualizar o grupo com a cópia limpa
      const updatedGroup = this.safeClone(this.group);
      updatedGroup.children[index].value = actualValue;
      this.$emit("change", updatedGroup, { updateEstimate: true, valueChanged: true });
    },

    // Quando recebemos uma atualização de um grupo filho, atualizamos apenas esse grupo
    updateFromChildGroup(updatedChildGroup, childIndex, options) {
      // Primeiro, remover propriedades reativas do Vue do grupo filho atualizado
      const cleanChildGroup = this.safeClone(updatedChildGroup);
      const updatedGroup = this.safeClone(this.group);
      updatedGroup.children[childIndex] = cleanChildGroup;

      const shouldUpdateEstimate = options && options.updateEstimate;

      this.$emit("change", updatedGroup, { updateEstimate: shouldUpdateEstimate });
    },

    // Update operator without mutating the prop
    updateOperator(event) {
      const updatedGroup = this.safeClone(this.group);
      updatedGroup.operator = event.target.value;
      this.$emit("change", updatedGroup, { updateEstimate: true });
    },

    addFilterToGroup(filter) {
      let filterValue = null;
      const updatedGroup = this.safeClone(this.group);
      updatedGroup.children.push({
        type: "filter",
        name: filter.name,
        value: filterValue,
      });

      this.$emit("change", updatedGroup, { updateEstimate: true, forceRefresh: true });

      this.showFilterSelector = false;
    },

    closeFilterSelector() {
      this.showFilterSelector = false;
    },
  }
};
</script>

<style lang="scss" scoped>
:root {
  --kevi-green: #11875A;
  --kevi-green-dark: #0d6a44; /* Substituindo darken(#11875A, 5%) */
}

.filter-group {
  border: 1px solid #e9ecef;
  border-radius: 8px;
  margin-bottom: 24px;
  background-color: #F7F9FA;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
  position: relative;

  &.is-nested {
    margin: 8px 0;
  }

  &.not-group {
    border-color: #ffe5e5;
    background-color: rgba(255, 242, 242, 0.5);

    .group-header {
      background-color: #fff2f2;
      border-bottom-color: #ffe5e5;
    }
  }
}

.group-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px;
  border-bottom: 1px solid #e9ecef;
  background-color: white;
  border-top-left-radius: 8px;
  border-top-right-radius: 8px;

  @media (max-width: 767px) {
    flex-direction: column;
    align-items: stretch;
    gap: 16px;
  }
}

.operator-selector {
  display: flex;
  align-items: center;

  .operator-select {
    padding: 8px 12px;
    border: 1px solid #ced4da;
    border-radius: 4px;
    font-size: 14px;
    font-weight: 500;
    color: #495057;
    min-width: 220px;
    background-color: white;

    &:focus {
      border-color: #11875A;
      box-shadow: 0 0 0 0.2rem rgba(17, 135, 90, 0.25);
    }
  }
}

.group-actions {
  display: flex;
  gap: 12px;

  @media (max-width: 767px) {
    justify-content: flex-end;
  }

  .action-wrapper {
    position: relative;
  }

  .btn-add-item {
    border-color: #11875A;
    color: #11875A;
    padding: 4px;
    height: auto;
    &:hover, &:focus {
      background-color: #11875A;
      color: white;
    }
  }

  .btn-remove {
    font-size: 14px;
    padding: 4px 8px;
    height: auto;
    border-color: #dc3545;
    color: #dc3545;

    &:hover, &:focus {
      background-color: #dc3545;
      color: white;
    }
  }

  // Seletor de filtros para subgrupos dentro do action-wrapper
  .filter-selector-dropdown {
    position: absolute;
    min-width: 250px;
    max-width: 320px;
    background-color: white;
    border-radius: 8px;
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
    z-index: 1050;
    overflow: hidden;
    top: 100%;
    left: 0;
    margin-top: 4px;

    .filter-selector-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 12px 16px;
      border-bottom: 1px solid #e9ecef;

      h6 {
        margin: 0;
        font-weight: 600;
        font-size: 14px;
      }

      .btn-close {
        font-size: 10px;
        padding: 8px;
        background-color: transparent;
        border: none;
        cursor: pointer;
        opacity: 0.5;

        &:hover {
          opacity: 1;
        }
      }
    }

    .filter-selector-body {
      max-height: 320px;
      overflow-y: auto;

      .no-filters {
        padding: 16px;
        text-align: center;
        color: #6c757d;
        font-size: 14px;
      }

      .filter-selector-list {
        list-style: none;
        padding: 0;
        margin: 0;

        .filter-selector-item {
          padding: 10px 16px;
          cursor: pointer;
          font-size: 14px;
          border-bottom: 1px solid #f5f5f5;

          &:hover {
            background-color: #f8f9fa;
          }

          &:last-child {
            border-bottom: none;
          }
        }
      }
    }
  }
}

.group-body {
  padding: 16px;
}

.filter-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 16px;

  @media (min-width: 992px) {
    grid-template-columns: repeat(2, 1fr);
  }
}

.filter-grid-item {
  width: 100%;
}

.filter-grid-item-full {
  width: 100%;
  grid-column: 1 / -1; /* Faz o item ocupar todas as colunas disponíveis no grid */
}

.empty-group {
  padding: 24px 0;
  text-align: center;

  .empty-message {
    color: #6c757d;
    font-size: 14px;
    background-color: white;
    border-radius: 8px;
    padding: 16px;
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.05);
  }
}

.filter-card {
  background-color: white;
  border-radius: 8px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.05);
  overflow: visible;
  position: relative;
  height: 100%;
  display: flex;
  flex-direction: column;

  .filter-card-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 12px 16px;
    background-color: white;
    border-bottom: 1px solid #f0f0f0;
    border-top-left-radius: 8px;
    border-top-right-radius: 8px;

    .filter-name {
      font-weight: 600;
      font-size: 14px;
      color: #333;
      margin: 0;
    }

    .btn-icon {
      width: 32px;
      height: 32px;
      border-radius: 4px;
      background: transparent;
      border: none;
      display: flex;
      align-items: center;
      justify-content: center;
      color: #adb5bd;
      transition: all 0.2s;
      padding: 0;

      &:hover, &:focus {
        color: #F44336;
        background-color: rgba(244, 67, 54, 0.08);
      }
    }
  }

  .filter-card-body {
    padding: 16px;
    flex-grow: 1;
  }
}

// Estilos específicos para os filtros internos
::v-deep .filter-label {
  cursor: pointer;
  color: #495057;
  font-weight: 500;

  &:hover {
    color: #11875A;
  }
}

::v-deep .filter-options {
  margin-top: 8px;
  border-radius: 8px;
  border: 1px solid #e9ecef;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);

  .filter-actions {
    margin-top: 16px;
    display: flex;
    justify-content: flex-end;
    gap: 8px;

    .btn-success {
      background-color: #11875A;
      border-color: #11875A;

      &:hover {
        background-color: var(--kevi-green-dark);
      }
    }
  }
}
</style>
